import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link, Navigate } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { useProject } from "@/components/project-context";
import { useProjectRole } from "@/lib/use-project-role";
import { TeamOwnerAccessNote } from "@/components/team-owner-access-note";
import { FullPageModal } from "@/components/layout/full-page-modal";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Chip } from "@/components/ui/chip";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { DangerZone } from "@/components/layout/danger-zone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PriorityWeightsSection } from "@/components/settings/priority-weights-section";
import { QualityGateSection } from "@/components/settings/quality-gate-section";
import { RoutePatternsSection } from "@/components/settings/route-patterns-section";
import { OwnershipRulesSection } from "@/components/settings/ownership-rules-section";
import { TrackerTargetSection } from "@/components/settings/tracker-target-section";
import { useProjectAccess } from "@/lib/project-access";
import { toast } from "sonner";
import {
  Trash2,
  UserPlus,
  Clock,
  Users,
  CheckCircle2,
  RotateCw,
  Copy,
} from "lucide-react";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { SlackIcon } from "@/components/icons";
import {
  AriaGridList,
  AriaGridListItem,
  AriaGridListAction,
} from "@/components/ui/aria-gridlist";
import { ScrollFade } from "@/components/ui/scroll-fade";

/* ─── Section definitions ─── */

const settingsSections = [
  {
    id: "general",
    label: "General",
    description: "Project name, product URL, and linked repository.",
  },
  {
    id: "team",
    label: "Team",
    description: "Manage project collaborators and invitations.",
  },
  {
    id: "tracker",
    label: "Tracker target",
    description:
      "Map this project to an external issue tracker (GitHub Issues, Linear, Jira, or Azure DevOps) to create issues and sync status.",
  },
  {
    id: "slack",
    label: "Slack notifications",
    description:
      "Configure an incoming Slack webhook for walkthrough run notifications and regression alerts.",
  },
  {
    id: "priority",
    label: "Priority",
    description:
      "How the Findings queue ranks problems. Every score shows its factors, so the order can be explained.",
  },
  {
    id: "route-patterns",
    label: "Route patterns",
    description:
      "How step URLs group into pages on the hotspot map. Suggestions are only applied when you accept them.",
  },
  {
    id: "quality-gate",
    label: "Quality gate",
    description:
      "How builds are gated. Configured in iamu/quality-gate.yaml; read-only here.",
  },
  {
    id: "danger",
    label: "Danger zone",
    description: "Irreversible actions.",
  },
];

void OwnershipRulesSection;

/* ─── Page ─── */

/**
 * Viewers read settings but can't change them.
 * Deleting the project is restricted to owners.
 */
function visibleSections(access: { canEdit: boolean; isOwner: boolean }) {
  return settingsSections.filter((s) => {
    if (s.id === "danger") return access.isOwner;
    return true;
  });
}

export function ProjectSettingsPage() {
  const project = useProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const access = useProjectAccess();
  const sections = visibleSections(access);
  const navigate = useNavigate();
  const validSectionIds = settingsSections.map((s) => s.id);
  const scrollTo = rawTab && validSectionIds.includes(rawTab) ? rawTab : null;
  const [activeSection, setActiveSection] = useState(scrollTo ?? "general");
  const manualSelectUntilRef = useRef(0);

  useEffect(() => {
    if (!scrollTo) return;
    manualSelectUntilRef.current = Date.now() + 400;
    setActiveSection(scrollTo);
    const el = document.getElementById(`section-${scrollTo}`);
    el?.scrollIntoView({ behavior: "instant", block: "start" });
  }, [scrollTo]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < manualSelectUntilRef.current) return;
        const intersecting = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (intersecting[0]) {
          setActiveSection(intersecting[0].target.id.replace("section-", ""));
        }
      },
      { rootMargin: "-96px 0px -75% 0px", threshold: 0 },
    );

    for (const section of settingsSections) {
      const el = document.getElementById(`section-${section.id}`);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  const selectTab = (tabId: string) => {
    manualSelectUntilRef.current = Date.now() + 400;
    setActiveSection(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
    const el = document.getElementById(`section-${tabId}`);
    el?.scrollIntoView({ behavior: "instant", block: "start" });
  };

  const handleClose = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/projects/${project.id}`);
    }
  };

  if (rawTab === "guidance") {
    return <Navigate to={`/projects/${project.id}/context`} replace />;
  }

  return (
    <FullPageModal
      title="Project settings"
      subtitle={project.name}
      onClose={handleClose}
      stickyHeader={true}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
        {/* Horizontal navigation for screens below lg */}
        <div className="sticky top-[73px] sm:top-[84px] z-20 -mx-4 sm:-mx-6 mb-8 border-b bg-background px-4 sm:px-6 py-2 lg:hidden">
          <ScrollFade className="flex gap-2 py-1">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => selectTab(section.id)}
                className={cn(
                  "rounded-md px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors",
                  activeSection === section.id
                    ? "bg-foreground text-background font-semibold shadow-xs"
                    : "bg-muted text-muted-foreground hover:text-foreground",
                )}
              >
                {section.label}
              </button>
            ))}
          </ScrollFade>
        </div>

        <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
          {/* In-flow vertical nav sidebar */}
          <nav
            className="sticky top-24 pt-2 hidden space-y-1 self-start lg:block"
            aria-label="Settings navigation"
          >
            <div className="mb-2 px-3 text-xs font-medium text-muted-foreground">
              Settings
            </div>
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => selectTab(section.id)}
                className={cn(
                  "flex w-full items-center rounded-md px-3 py-2 text-xs font-medium transition-colors text-left",
                  activeSection === section.id
                    ? "bg-accent text-accent-foreground font-semibold"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                {section.label}
              </button>
            ))}
          </nav>

          {/* Main sections column */}
          <div className="min-w-0 space-y-12 pb-24">
            {sections.map((section) => (
              <section
                key={section.id}
                id={`section-${section.id}`}
                className="scroll-mt-36 sm:scroll-mt-32 space-y-4"
              >
                <div className="border-b border-border/60 pb-3">
                  <h2 className="text-base font-semibold tracking-tight">
                    {section.label}
                  </h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {section.description}
                  </p>
                </div>
                {section.id === "general" && (
                  <GeneralSection project={project} />
                )}
                {section.id === "team" && (
                  <TeamSection projectId={project.id} />
                )}
                {section.id === "tracker" && (
                  <TrackerTargetSection projectId={project.id} />
                )}
                {section.id === "slack" && (
                  <SlackWebhookSection projectId={project.id} />
                )}
                {section.id === "priority" && (
                  <PriorityWeightsSection projectId={project.id} />
                )}
                {section.id === "quality-gate" && (
                  <QualityGateSection projectId={project.id} />
                )}
                {section.id === "route-patterns" && (
                  <RoutePatternsSection projectId={project.id} />
                )}
                {section.id === "danger" && (
                  <DangerZoneSection project={project} />
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </FullPageModal>
  );
}

/* ─── General ─── */

type GeneralProject = {
  id: string;
  name: string;
  productUrl?: string | null;
  githubRepositoryId?: string | null;
};

function GeneralSection({ project }: { project: GeneralProject }) {
  const { canEdit } = useProjectAccess();
  return canEdit ? (
    <GeneralForm project={project} />
  ) : (
    <GeneralReadOnly project={project} />
  );
}

function GeneralReadOnly({ project }: { project: GeneralProject }) {
  const rows = [
    { label: "Name", value: project.name },
    { label: "Product URL", value: project.productUrl },
  ];
  return (
    <Card>
      <CardContent className="pt-6">
        <dl className="max-w-lg space-y-4">
          {rows.map((row) => (
            <div key={row.label} className="space-y-1">
              <dt className="text-sm font-medium text-muted-foreground">
                {row.label}
              </dt>
              <dd className="text-base text-foreground">
                {row.value || "Not set"}
              </dd>
            </div>
          ))}
          <div className="space-y-1 pt-2 border-t border-border/60">
            <dt className="text-sm font-medium text-muted-foreground">
              Product Context &amp; Narrative
            </dt>
            <dd className="text-sm text-muted-foreground">
              Description and domain knowledge that ground AI prompts are
              managed in{" "}
              <Link
                to={`/projects/${project.id}/context`}
                className="font-medium text-primary hover:underline"
              >
                Project Context →
              </Link>
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

function GeneralForm({ project }: { project: GeneralProject }) {
  const utils = trpc.useUtils();
  const [editName, setEditName] = useState(project.name);
  const [editProductUrl, setEditProductUrl] = useState(
    project.productUrl ?? "",
  );
  const [editGithubRepoId, setEditGithubRepoId] = useState(
    project.githubRepositoryId ?? "none",
  );
  const [dirty, setDirty] = useState(false);

  const { data: githubRepos } = trpc.github.listRepositories.useQuery({});

  const updateMutation = trpc.project.update.useMutation({
    onSuccess: () => {
      toast.success("Project updated");
      utils.project.getById.invalidate({ id: project.id });
      utils.project.list.invalidate();
      setDirty(false);
    },
    onError: (err) => toastTrpcError(err, "Failed to update project"),
  });

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="max-w-lg space-y-5">
            <div className="space-y-2">
              <Label htmlFor="settings-name">Name</Label>
              <Input
                id="settings-name"
                value={editName}
                onChange={(e) => {
                  setEditName(e.target.value);
                  setDirty(true);
                }}
              />
            </div>
            <div className="rounded-md bg-muted/40 p-3.5 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">
                Product Context &amp; Narrative
              </div>
              <p className="mt-1 leading-relaxed">
                Product description, domain boundaries, and problem space ground
                all AI walkthrough prompts and are managed in{" "}
                <Link
                  to={`/projects/${project.id}/context`}
                  className="font-medium text-primary hover:underline"
                >
                  Project Context →
                </Link>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-product-url">Product URL</Label>
              <Input
                id="settings-product-url"
                value={editProductUrl}
                onChange={(e) => {
                  setEditProductUrl(e.target.value);
                  setDirty(true);
                }}
                placeholder="https://example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-github-repo">GitHub repository</Label>
              {githubRepos && githubRepos.length > 0 ? (
                <>
                  <Select
                    value={editGithubRepoId}
                    onValueChange={(value) => {
                      setEditGithubRepoId(value);
                      setDirty(true);
                    }}
                  >
                    <SelectTrigger id="settings-github-repo" className="w-full">
                      <SelectValue placeholder="Select a repository" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {project.githubRepositoryId &&
                        !githubRepos.some(
                          (r) => r.id === project.githubRepositoryId,
                        ) && (
                          <SelectItem
                            value={project.githubRepositoryId}
                            disabled
                          >
                            Current repository (Access lost)
                          </SelectItem>
                        )}
                      {githubRepos.map((repo) => (
                        <SelectItem key={repo.id} value={repo.id}>
                          {repo.fullName}
                          {repo.isPrivate ? " (Private)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {project.githubRepositoryId &&
                    !githubRepos.some(
                      (r) => r.id === project.githubRepositoryId,
                    ) && (
                      <p className="text-xs text-amber-500">
                        ⚠ The currently linked repository is no longer
                        accessible via your GitHub App. Re-authorize the
                        installation in Integrations or pick another repository.
                      </p>
                    )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No GitHub App connected.{" "}
                  <Link
                    to="/settings?tab=integrations"
                    className="underline hover:text-foreground"
                  >
                    Set up integration
                  </Link>
                </p>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex items-center justify-between border-t border-border/60 bg-muted/20 px-6 py-3">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <Button
            size="sm"
            onClick={() =>
              updateMutation.mutate({
                id: project.id,
                name: editName.trim() || undefined,
                productUrl: editProductUrl.trim() || null,
                githubRepositoryId:
                  editGithubRepoId === "none" ? null : editGithubRepoId,
              })
            }
            disabled={!dirty || updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}

/* ─── Team ─── */

function TeamSection({ projectId }: { projectId: string }) {
  const { auth } = useAuth();
  const utils = trpc.useUtils();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"editor" | "viewer">("editor");
  const [selectedWorkspaceUserId, setSelectedWorkspaceUserId] =
    useState<string>("");
  const [inviteMode, setInviteMode] = useState<"workspace" | "email">(
    "workspace",
  );

  const {
    data: members,
    isLoading: membersLoading,
    isError: membersError,
    refetch: refetchMembers,
  } = trpc.project.members.useQuery({ projectId });

  const { isOwner } = useProjectRole(projectId);

  const { data: workspaceMembers } = trpc.tenant.members.useQuery(undefined, {
    enabled: isOwner,
  });

  const availableWorkspaceMembers = (workspaceMembers ?? []).filter(
    (wm) => !members?.some((pm) => pm.userId === wm.userId),
  );

  // Owner-only on the API; don't ask as anyone else.
  const { isOwner: ownsProject } = useProjectAccess();
  const { data: pendingInvites } = trpc.project.pendingInvites.useQuery(
    { projectId },
    { enabled: ownsProject },
  );

  const addMemberMutation = trpc.project.addMember.useMutation({
    onSuccess: () => {
      toast.success("Member added to project");
      utils.project.members.invalidate({ projectId });
      setShowInvite(false);
      setSelectedWorkspaceUserId("");
      setInviteRole("editor");
    },
    onError: (err) => toastTrpcError(err, "Failed to add member"),
  });

  const inviteMutation = trpc.project.invite.useMutation({
    onSuccess: (result) => {
      if (result.existing) {
        toast.info("An invite already exists for this email");
      } else {
        toast.success("Invite sent");
      }
      const link = `${window.location.origin}/invite/${result.token}`;
      navigator.clipboard.writeText(link).then(
        () => toast.info("Invite link copied to clipboard"),
        () => toast.info(`Invite link: ${link}`),
      );
      utils.project.pendingInvites.invalidate({ projectId });
      setShowInvite(false);
      setInviteEmail("");
      setInviteRole("editor");
    },
    onError: (err) => toastTrpcError(err, "Failed to send invite"),
  });

  const removeMutation = trpc.project.removeMember.useMutation({
    onSuccess: () => {
      toast.success("Member removed");
      utils.project.members.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to remove member"),
  });

  const updateRoleMutation = trpc.project.updateMemberRole.useMutation({
    onSuccess: () => {
      toast.success("Role updated");
      utils.project.members.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to update member role"),
  });

  const revokeInviteMutation = trpc.project.revokeInvite.useMutation({
    onSuccess: () => {
      toast.success("Invite revoked");
      utils.project.pendingInvites.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to revoke invite"),
  });

  const resendInviteMutation = trpc.project.resendInvite.useMutation({
    onSuccess: (result) => {
      toast.success("Invite resent");
      const link = `${window.location.origin}/invite/${result.token}`;
      navigator.clipboard.writeText(link).then(
        () => toast.info("New invite link copied to clipboard"),
        () => toast.info(`Invite link: ${link}`),
      );
      utils.project.pendingInvites.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to resend invite"),
  });

  return (
    <>
      <Card>
        <CardContent className="pt-6 space-y-6">
          {isOwner && (
            <div>
              <Button
                onClick={() => {
                  setInviteMode(
                    availableWorkspaceMembers.length > 0
                      ? "workspace"
                      : "email",
                  );
                  setShowInvite(true);
                }}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Add Member
              </Button>
            </div>
          )}

          <div>
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">
              <Users className="mr-1.5 inline h-4 w-4" />
              Members {members ? `(${members.length})` : ""}
            </h3>
            {membersLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : membersError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center space-y-2">
                <p className="text-sm text-destructive font-medium">
                  Failed to load members
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchMembers()}
                >
                  Try again
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border/60 border-y border-border/60">
                <AriaGridList aria-label="Project members">
                  {(members ?? []).map((member) => (
                    <AriaGridListItem
                      key={member.id}
                      id={member.id}
                      textValue={member.displayName ?? "Unknown"}
                      className="py-2.5 px-3 hover:bg-muted/40"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {member.avatarUrl ? (
                            <img
                              src={member.avatarUrl}
                              alt=""
                              className="h-8 w-8 rounded-full"
                            />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-medium">
                              {(member.displayName ?? "?")[0]?.toUpperCase()}
                            </div>
                          )}
                          <span className="text-sm font-medium">
                            {member.displayName ?? "Unknown"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {isOwner &&
                          member.role !== "owner" &&
                          member.userId !== auth?.userId ? (
                            <>
                              <Select
                                value={member.role}
                                onValueChange={(role) =>
                                  updateRoleMutation.mutate({
                                    projectId,
                                    userId: member.userId,
                                    role: role as "editor" | "viewer",
                                  })
                                }
                              >
                                <SelectTrigger
                                  className="h-8 w-24"
                                  aria-label={`Role of ${member.displayName ?? "member"}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="editor">Editor</SelectItem>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                </SelectContent>
                              </Select>
                              <AriaGridListAction
                                aria-label={`Remove ${member.displayName || "member"}`}
                                onPress={() =>
                                  removeMutation.mutate({
                                    projectId,
                                    userId: member.userId,
                                  })
                                }
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </AriaGridListAction>
                            </>
                          ) : (
                            <Chip tone="outline">
                              {member.role.charAt(0).toUpperCase() +
                                member.role.slice(1)}
                            </Chip>
                          )}
                        </div>
                      </div>
                    </AriaGridListItem>
                  ))}
                </AriaGridList>
              </div>
            )}
            <TeamOwnerAccessNote />
          </div>

          {isOwner && pendingInvites && pendingInvites.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                <Clock className="mr-1.5 inline h-4 w-4" />
                Pending Invites ({pendingInvites.length})
              </h3>
              <div className="divide-y rounded-lg border">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div>
                      <p className="text-sm">{invite.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Expires{" "}
                        {invite.expiresAt
                          ? new Date(invite.expiresAt).toLocaleDateString()
                          : "never"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-xs mr-1">
                        {invite.role ?? "editor"}
                      </Badge>
                      <SimpleTooltip content="Resend invite">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          aria-label={`Resend invite for ${invite.email}`}
                          disabled={resendInviteMutation.isPending}
                          onClick={() =>
                            resendInviteMutation.mutate({ inviteId: invite.id })
                          }
                        >
                          <RotateCw
                            className={cn(
                              "h-3.5 w-3.5",
                              resendInviteMutation.isPending && "animate-spin",
                            )}
                          />
                        </Button>
                      </SimpleTooltip>
                      <SimpleTooltip content="Copy invite link">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          aria-label={`Copy invite link for ${invite.email}`}
                          onClick={() => {
                            if (invite.token) {
                              const link = `${window.location.origin}/invite/${invite.token}`;
                              navigator.clipboard.writeText(link);
                              toast.info("Invite link copied to clipboard");
                            } else {
                              toast.error(
                                "Invite token unavailable. Please resend the invite.",
                              );
                            }
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </SimpleTooltip>
                      <SimpleTooltip content="Revoke invite">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          aria-label={`Revoke invite for ${invite.email}`}
                          onClick={() =>
                            revokeInviteMutation.mutate({ inviteId: invite.id })
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </SimpleTooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Project</DialogTitle>
            <DialogDescription>
              Add existing workspace members or invite an external collaborator
              to this project. Collaborators invited here only see this project.
            </DialogDescription>
          </DialogHeader>

          {availableWorkspaceMembers.length > 0 ? (
            <Tabs
              value={inviteMode}
              onValueChange={(v) => setInviteMode(v as "workspace" | "email")}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="workspace">
                  Workspace ({availableWorkspaceMembers.length})
                </TabsTrigger>
                <TabsTrigger value="email">Invite by Email</TabsTrigger>
              </TabsList>

              <TabsContent value="workspace" className="space-y-4 pt-3">
                <div className="space-y-2">
                  <Label htmlFor="settings-workspace-member">
                    Workspace Member
                  </Label>
                  <Select
                    value={selectedWorkspaceUserId}
                    onValueChange={setSelectedWorkspaceUserId}
                  >
                    <SelectTrigger id="settings-workspace-member">
                      <SelectValue placeholder="Select a workspace member" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableWorkspaceMembers.map((member) => (
                        <SelectItem key={member.userId} value={member.userId}>
                          {member.displayName ?? "Unknown"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="settings-workspace-role">Role</Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(v) =>
                      setInviteRole(v as "editor" | "viewer")
                    }
                  >
                    <SelectTrigger id="settings-workspace-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">
                        Editor — can create and modify content
                      </SelectItem>
                      <SelectItem value="viewer">
                        Viewer — read-only access
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowInvite(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (!selectedWorkspaceUserId) return;
                      addMemberMutation.mutate({
                        projectId,
                        userId: selectedWorkspaceUserId,
                        role: inviteRole,
                      });
                    }}
                    disabled={
                      !selectedWorkspaceUserId || addMemberMutation.isPending
                    }
                  >
                    {addMemberMutation.isPending
                      ? "Adding..."
                      : "Add to Project"}
                  </Button>
                </DialogFooter>
              </TabsContent>

              <TabsContent value="email" className="space-y-4 pt-3">
                <div className="space-y-2">
                  <Label htmlFor="settings-invite-email">Email</Label>
                  <Input
                    id="settings-invite-email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="settings-invite-role">Role</Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(v) =>
                      setInviteRole(v as "editor" | "viewer")
                    }
                  >
                    <SelectTrigger id="settings-invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">
                        Editor — can create and modify content
                      </SelectItem>
                      <SelectItem value="viewer">
                        Viewer — read-only access
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowInvite(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (!inviteEmail.trim()) return;
                      inviteMutation.mutate({
                        projectId,
                        email: inviteEmail.trim(),
                        role: inviteRole,
                      });
                    }}
                    disabled={!inviteEmail.trim() || inviteMutation.isPending}
                  >
                    {inviteMutation.isPending ? "Sending..." : "Send Invite"}
                  </Button>
                </DialogFooter>
              </TabsContent>
            </Tabs>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="settings-invite-email">Email</Label>
                  <Input
                    id="settings-invite-email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="settings-invite-role">Role</Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(v) =>
                      setInviteRole(v as "editor" | "viewer")
                    }
                  >
                    <SelectTrigger id="settings-invite-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">
                        Editor — can create and modify content
                      </SelectItem>
                      <SelectItem value="viewer">
                        Viewer — read-only access
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInvite(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!inviteEmail.trim()) return;
                    inviteMutation.mutate({
                      projectId,
                      email: inviteEmail.trim(),
                      role: inviteRole,
                    });
                  }}
                  disabled={!inviteEmail.trim() || inviteMutation.isPending}
                >
                  {inviteMutation.isPending ? "Sending..." : "Send Invite"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─── Slack Webhook ─── */

function SlackWebhookSection({ projectId }: { projectId: string }) {
  const { canEdit } = useProjectAccess();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.project.getSlackWebhook.useQuery({
    projectId,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (data?.slackWebhookUrl) {
      setWebhookUrl(data.slackWebhookUrl);
      setIsDirty(false);
    }
  }, [data?.slackWebhookUrl]);

  const updateMutation = trpc.project.updateSlackWebhook.useMutation({
    onSuccess: () => {
      toast.success("Slack webhook URL updated");
      setIsDirty(false);
      utils.project.getSlackWebhook.invalidate({ projectId });
    },
    onError: (err) => toastTrpcError(err, "Failed to update Slack webhook"),
  });

  const testMutation = trpc.project.testSlackWebhook.useMutation({
    onSuccess: () => {
      toast.success("Test notification sent to Slack successfully!");
    },
    onError: (err) =>
      toastTrpcError(err, "Failed to send test notification to Slack"),
  });

  const isConfigured = Boolean(data?.slackWebhookUrl);

  if (isLoading) {
    return <Skeleton className="h-40 w-full rounded-xl" />;
  }

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SlackIcon className="h-5 w-5" />
            <span className="font-semibold text-sm">Incoming Webhook</span>
          </div>
          {isConfigured ? (
            <Chip tone="helps">Configured</Chip>
          ) : (
            <Chip tone="muted">Not configured</Chip>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Iamu sends Block Kit formatted alerts directly to your Slack channel
          when automated walkthrough runs complete or regressions occur.
        </p>

        <div className="space-y-2">
          <Label htmlFor="slack-webhook-url">Webhook URL</Label>
          <Input
            id="slack-webhook-url"
            type="url"
            placeholder="https://hooks.slack.com/services/T.../B.../X..."
            value={webhookUrl}
            onChange={(e) => {
              setWebhookUrl(e.target.value);
              setIsDirty(true);
            }}
            disabled={!canEdit || updateMutation.isPending}
            className="font-mono text-xs"
          />
        </div>

        <div className="space-y-2 pt-2 border-t border-border/50">
          <Label className="text-xs font-semibold text-muted-foreground">
            Active alert triggers
          </Label>
          <div className="grid gap-2.5 sm:grid-cols-2">
            <div className="flex items-start gap-2.5 rounded-md bg-muted/30 p-3">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-foreground">
                  Walkthrough Run Completed
                </div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  Summary with persona, run duration, and finding counts (helps,
                  hinders, regressions).
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 rounded-md bg-muted/30 p-3">
              <CheckCircle2 className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-foreground">
                  Regression Detected
                </div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                  Immediate critical alert with consequence and direct link when
                  a fixed finding re-occurs.
                </div>
              </div>
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            {isConfigured && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  updateMutation.mutate({
                    projectId,
                    slackWebhookUrl: null,
                  });
                  setWebhookUrl("");
                }}
                disabled={updateMutation.isPending}
              >
                Remove
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                testMutation.mutate({
                  projectId,
                  slackWebhookUrl: webhookUrl.trim() || undefined,
                })
              }
              disabled={
                testMutation.isPending || (!isConfigured && !webhookUrl.trim())
              }
            >
              {testMutation.isPending ? "Sending Test..." : "Send Test Alert"}
            </Button>
            <Button
              onClick={() =>
                updateMutation.mutate({
                  projectId,
                  slackWebhookUrl: webhookUrl.trim() || null,
                })
              }
              disabled={!isDirty || updateMutation.isPending}
              size="sm"
            >
              {updateMutation.isPending ? "Saving..." : "Save Webhook"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Danger Zone ─── */

function DangerZoneSection({
  project,
}: {
  project: { id: string; name: string };
}) {
  const utils = trpc.useUtils();
  const navigate = useNavigate();

  const deleteMutation = trpc.project.delete.useMutation({
    onSuccess: () => {
      navigate("/projects");
      toast.success("Project deleted");
      utils.project.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to delete project"),
  });

  return (
    <DangerZone
      title="Delete this project"
      description={`Permanently delete “${project.name}” and all its data. This cannot be undone.`}
      actionLabel="Delete project"
      confirmTitle="Delete this project?"
      confirmDescription={`This will permanently delete “${project.name}” and all its data. This cannot be undone.`}
      onConfirm={() => deleteMutation.mutate({ id: project.id })}
      isPending={deleteMutation.isPending}
    />
  );
}
